import React, { useState, useMemo } from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import { toast } from "react-toastify";
import {
  DocumentDuplicateIcon,
  EyeIcon,
  EyeSlashIcon,
  KeyIcon,
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import Button from "src/components/Shared/Buttons/Button";

export const CustomAppEnvVars = ({ data }) => {
  const [showValues, setShowValues] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const envVars = data?.envVars || {};
  const entries = useMemo(() => Object.entries(envVars), [envVars]);

  const filteredEntries = useMemo(() => {
    if (!searchTerm.trim()) return entries;
    const q = searchTerm.toLowerCase();
    return entries.filter(([key]) => key.toLowerCase().includes(q));
  }, [entries, searchTerm]);

  const isSensitiveKey = (key) => {
    const k = key.toUpperCase();
    return (
      k.includes("SECRET") ||
      k.includes("KEY") ||
      k.includes("TOKEN") ||
      k.includes("PASS") ||
      k.includes("AUTH")
    );
  };

  const maskValue = (val) => {
    if (!val) return "—";
    if (val.length <= 8) return "••••••••";
    return val.slice(0, 4) + "••••••••" + val.slice(-4);
  };

  const envFileContent = useMemo(() => {
    return entries.map(([k, v]) => `${k}="${v}"`).join("\n");
  }, [entries]);

  return (
    <div className="animate-fade-in space-y-4 pt-2">
      {/* Header Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-2xs">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
            <KeyIcon className="h-5 w-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold text-gray-900">
                Environment Variables
              </h3>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">
                {entries.length} keys
              </span>
            </div>
            <p className="text-xs text-gray-500">
              Injected runtime variables available to container processes
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          {/* Search Input */}
          <div className="relative">
            <input
              type="text"
              placeholder="Filter keys..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-9 w-40 sm:w-52 rounded-xl border border-gray-200 bg-slate-50/50 pl-8 pr-3 text-xs text-gray-800 placeholder-gray-400 focus:bg-white focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
            <MagnifyingGlassIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          </div>

          {/* Reveal/Mask Toggle */}
          <button
            type="button"
            onClick={() => setShowValues(!showValues)}
            className="flex h-9 items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-700 shadow-2xs hover:bg-gray-50 hover:border-gray-300 transition-all"
          >
            {showValues ? (
              <>
                <EyeSlashIcon className="h-4 w-4 text-gray-500" />
                <span>Mask</span>
              </>
            ) : (
              <>
                <EyeIcon className="h-4 w-4 text-gray-500" />
                <span>Reveal</span>
              </>
            )}
          </button>

          {/* Copy All as .env */}
          {entries.length > 0 && (
            <CopyToClipboard
              text={envFileContent}
              onCopy={() => toast.success("Exported all variables to clipboard (.env format)")}
            >
              <Button
                type="button"
                version="default"
                className="flex !h-9 items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-700 shadow-2xs hover:bg-gray-50 hover:border-gray-300"
                title="Copy all variables in .env file format"
              >
                <ArrowDownTrayIcon className="h-4 w-4 text-gray-500" />
                <span className="hidden sm:inline">Export .env</span>
              </Button>
            </CopyToClipboard>
          )}
        </div>
      </div>

      {/* Table Card */}
      {filteredEntries.length > 0 ? (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xs">
          <table className="min-w-full divide-y divide-gray-100 text-left">
            <thead className="bg-slate-50/70">
              <tr>
                <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  Variable Key
                </th>
                <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  Assigned Value
                </th>
                <th className="w-20 px-5 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 font-mono text-xs">
              {filteredEntries.map(([key, value]) => {
                const sensitive = isSensitiveKey(key);
                const displayValue =
                  showValues || !sensitive
                    ? String(value)
                    : maskValue(String(value));

                return (
                  <tr
                    key={key}
                    className="hover:bg-indigo-50/30 transition-colors group"
                  >
                    <td className="px-5 py-3.5 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors">
                          {key}
                        </span>
                        {sensitive && (
                          <span className="rounded-md bg-amber-50 px-1.5 py-0.5 text-[9px] font-sans font-bold text-amber-700 border border-amber-200 uppercase tracking-wider">
                            Secret
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-slate-600 max-w-lg break-all text-[11px]">
                      <span className="line-clamp-2 leading-relaxed">
                        {displayValue}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-right whitespace-nowrap">
                      <CopyToClipboard
                        text={String(value)}
                        onCopy={() => toast.success(`Copied ${key}`)}
                      >
                        <Button
                          type="button"
                          version="default"
                          className="inline-flex !h-8 !w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:text-indigo-600 hover:border-indigo-200 shadow-2xs group-hover:border-gray-300"
                          title="Copy value"
                        >
                          <DocumentDuplicateIcon className="h-3.5 w-3.5" />
                        </Button>
                      </CopyToClipboard>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-gray-200 p-12 text-center text-xs text-gray-400">
          {entries.length === 0
            ? "No environment variables configured for this application."
            : `No variables match "${searchTerm}"`}
        </div>
      )}
    </div>
  );
};
